import { Modal } from "antd";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IPatientInfo } from "../../../../@types/Patient";
import { Button } from "../../../../components/Button";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { useToast } from "../../../../hooks/useToast";
import { patientService } from "../../../../services/patientService";
import {
  DATE_VALUE_FORMAT,
  getPortalDateFormat,
} from "../../../../utils/portalDateFormat";
import {
  ButtonContainer,
  CancelButton,
  ContentContainer,
  ModalContainer,
  ModalTitle,
  PatientName,
  Row,
  ErrorMessage,
  contentTooLargemModalCss,
} from "./DownloadPatientData.styles";

dayjs.extend(utc);

type DownloadPatientDataProps = {
  patient: IPatientInfo;
  onClose: (success?: boolean, format?: string) => void;
};

export function DownloadPatientData(props: DownloadPatientDataProps) {
  const { t } = useTranslation();

  const { onClose, patient } = props;
  const { showToast } = useToast();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [isContentTooLargeError, setIsContentTooLargeError] = useState(false);
  const [format, setFormat] = useState("PDF");
  const [errorStartDate, setErrorStartDate] = useState("");
  const [errorEndDate, setErrorEndDate] = useState("");
  // EN-5272: the picker only ever commits a real calendar date (or null when cleared), so what is
  // left to check here is that both ends are set, neither is in the future, and they are in order.
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const dateFormat = getPortalDateFormat();
  const today = dayjs();

  const handleCancelClick = () => {
    onClose();
  };

  const getFutureDateError = (date: Dayjs) =>
    date.isAfter(dayjs(), "day") ? t("patientPage_download_futureDate") : "";

  const handleDownloadClick = async () => {
    if (startDate === null || endDate === null) {
      setErrorStartDate(
        startDate === null ? t("patientPage_download_invalidDate") : ""
      );
      setErrorEndDate(
        endDate === null ? t("patientPage_download_invalidDate") : ""
      );
      return;
    }

    const startDateError = getFutureDateError(startDate);
    const endDateError = getFutureDateError(endDate);

    setErrorStartDate(startDateError);
    setErrorEndDate(endDateError);

    if (startDateError || endDateError) {
      return;
    }

    if (endDate.isBefore(startDate, "day")) {
      setErrorEndDate(t("patientPage_download_endBeforeStart"));
      return;
    }

    setSubmitLoading(true);
    // The range bounds are calendar dates — the picker hands back local midnight, so re-read the
    // picked day as UTC to keep the dates serialized into the request independent of the user's
    // timezone.
    const result = await patientService.getPatientPdf(
      patient.id,
      dayjs.utc(startDate.format(DATE_VALUE_FORMAT), DATE_VALUE_FORMAT),
      dayjs.utc(endDate.format(DATE_VALUE_FORMAT), DATE_VALUE_FORMAT)
    );

    if (result.status === 413) {
      setSubmitLoading(false);
      setIsContentTooLargeError(true);
      return;
    }

    if (result.status === 200) {
      const contentDisposition = result.headers["content-disposition"];

      const fileName =
        contentDisposition?.match(/filename\*=UTF-8''(.+)/)?.[1] ||
        contentDisposition?.match(/filename=(.+);?/)?.[1] ||
        "Patient-Report.xlsx";

      const url = URL.createObjectURL(new Blob([result.data]));
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      });

      document.body.appendChild(link);
      link.click();
      link.remove();

      setSubmitLoading(false);
      onClose(true, format);
    } else {
      setSubmitLoading(false);
      onClose(false, format);
      showToast(t("patientPage_download_failed"), "error");
    }
  };

  const handleReportTooLargeModalClose = () => {
    setIsContentTooLargeError(false);
  };

  return (
    <>
      <ModalContainer>
        <ModalTitle>{t("patientPage_download_title")}</ModalTitle>
        <ContentContainer>
          <Row>
            <p>{t("patientPage_download_format")}</p>
            <Dropdown
              trigger={format}
              onValueChange={(e) => {
                setFormat(e);
              }}
              backgroundColor="#E6E6E6"
              fontColor="#212121"
              size="small"
            >
              <DropdownTextItem text="PDF" value="PDF" />
            </Dropdown>
          </Row>
          <Row>
            <p>{t("patientPage_download_patient")}</p>
            <PatientName>{patient.user.name}</PatientName>
          </Row>
          <Row dateField>
            <p>{t("patientPage_download_startDate")}</p>
            <DateTimePickerInput
              showTime={false}
              format={dateFormat}
              placeholder={dateFormat}
              width="164px"
              maxDate={today}
              value={startDate}
              // Clearing hands back null despite the signature.
              onChange={(value: Dayjs | null) => setStartDate(value)}
            />
            <ErrorMessage>{errorStartDate}</ErrorMessage>
          </Row>
          <Row dateField>
            <p>{t("patientPage_download_endDate")}</p>
            <DateTimePickerInput
              showTime={false}
              format={dateFormat}
              placeholder={dateFormat}
              width="164px"
              maxDate={today}
              value={endDate}
              onChange={(value: Dayjs | null) => setEndDate(value)}
            />
            <ErrorMessage>{errorEndDate}</ErrorMessage>
          </Row>
        </ContentContainer>
        <ButtonContainer>
          <Button
            onClick={handleDownloadClick}
            type="submit"
            label={
              submitLoading
                ? t("patientPage_download_downloading")
                : t("patientPage_download_download")
            }
            isLoading={submitLoading}
          />
          <CancelButton onClick={handleCancelClick}>
            {t("common_cancel")}
          </CancelButton>
        </ButtonContainer>
      </ModalContainer>

      {isContentTooLargeError ? (
        <Modal
          title={t("patientPage_download_tooLarge")}
          open
          zIndex={3005}
          styles={{ mask: { pointerEvents: "all" } }}
          footer={null}
          width={450}
          onCancel={handleReportTooLargeModalClose}
          className={contentTooLargemModalCss()}
          centered
          destroyOnClose
        >
          <ContentContainer>
            <p
              style={{
                padding: "18px 0 0 0",
                fontWeight: "400",
                color: "#939393",
                marginLeft: "5px",
              }}
            >
              {t("patientPage_download_tooLargeBody")}
            </p>
          </ContentContainer>
          <ButtonContainer>
            <Button
              label={t("patientPage_download_ok")}
              type="button"
              onClick={handleReportTooLargeModalClose}
            />
          </ButtonContainer>
        </Modal>
      ) : null}
    </>
  );
}
